/**
 * Projection du plan des pistes : vue parallèle oblique regardant vers le
 * nord depuis le village (sommets en haut, village en bas), relief exagéré,
 * cadrée sur le domaine skiable (remontées, pistes, village) avec une marge
 * et un ciel au-dessus des crêtes pour le cartouche.
 *
 * Écran (pixels CSS) : sx = (x - x0) * s ; sy = (z * sin - h * E * cos - y0) * s.
 * La profondeur se lit sur z : plus z est grand (sud), plus le point est
 * proche de l'observateur.
 */
import type { Heightfield, Vec2 } from '@snowchain/shared';

/** Inclinaison du regard sous l'horizontale. */
export const TILT = (32 * Math.PI) / 180;
/** Exagération du relief. */
export const RELIEF = 1.6;

export interface View {
  /** Pixels CSS par mètre. */
  s: number;
  /** Abscisse monde au bord gauche (m). */
  x0: number;
  /** Ordonnée projetée au bord haut (m). */
  y0: number;
  /** Taille de la vue (pixels CSS). */
  w: number;
  h: number;
  sin: number;
  cos: number;
  e: number;
}

/** Emprise au sol du domaine (m). */
export interface Domain {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Emprise des points donnés, élargie d'une marge (m). */
export function domainOf(points: Iterable<Vec2>, fallback: Vec2, margin: number): Domain {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const p of points) {
    if (p[0] < minX) minX = p[0];
    if (p[0] > maxX) maxX = p[0];
    if (p[1] < minZ) minZ = p[1];
    if (p[1] > maxZ) maxZ = p[1];
  }
  if (!Number.isFinite(minX)) {
    minX = fallback[0] - 500;
    maxX = fallback[0] + 500;
    minZ = fallback[1] - 900;
    maxZ = fallback[1] + 100;
  }
  return { minX: minX - margin, maxX: maxX + margin, minZ: minZ - margin, maxZ: maxZ + margin };
}

/** Ordonnée projetée (m, avant mise à l'échelle). */
export function projY(v: View, z: number, h: number): number {
  return z * v.sin - h * v.e * v.cos;
}

/** Point monde vers écran (pixels CSS). */
export function toScreen(v: View, x: number, z: number, h: number): [number, number] {
  return [(x - v.x0) * v.s, (z * v.sin - h * v.e * v.cos - v.y0) * v.s];
}

/**
 * Cadrage : le domaine tient dans la vue, avec un ciel en haut (`skyFrac`
 * de la hauteur) et une marge latérale. L'échelle est la plus grande qui
 * fait tout tenir ; le surplus est réparti de part et d'autre.
 */
export function makeView(hf: Heightfield, d: Domain, w: number, h: number, skyFrac = 0.16): View {
  const sin = Math.sin(TILT);
  const cos = Math.cos(TILT);
  const probe: View = { s: 1, x0: 0, y0: 0, w, h, sin, cos, e: RELIEF };
  let top = Infinity;
  let bottom = -Infinity;
  const n = 24;
  for (let j = 0; j <= n; j++) {
    for (let i = 0; i <= n; i++) {
      const x = d.minX + ((d.maxX - d.minX) * i) / n;
      const z = d.minZ + ((d.maxZ - d.minZ) * j) / n;
      const y = projY(probe, z, hf.height(x, z));
      if (y < top) top = y;
      if (y > bottom) bottom = y;
    }
  }
  const spanX = d.maxX - d.minX;
  const spanY = bottom - top;
  const usableH = h * (1 - skyFrac - 0.03);
  const s = Math.min((w * 0.96) / spanX, usableH / spanY);
  const cx = (d.minX + d.maxX) / 2;
  const x0 = cx - w / 2 / s;
  // Bas du domaine calé juste au-dessus du bord inférieur, ciel au-dessus.
  const free = usableH - spanY * s;
  const y0 = top - (h * skyFrac + free * 0.55) / s;
  return { s, x0, y0, w, h, sin, cos, e: RELIEF };
}
